/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  useEffect,
  useMemo,
} from 'react';
import {
  fetchVersesBatched,
} from '@utils/store/actions';
import useTypedDispatch from './useTypedDispatch';
import useTypedSelector from './useTypedSelector';

export default function useGetVersesForPassages(passages, skipFetch = false) {
  const dispatch = useTypedDispatch();
  const verses = useTypedSelector((state) => state.verses);
  const books = useTypedSelector((state) => state.books);

  const passagesKey = useMemo(() => JSON.stringify(passages), [passages]);

  useEffect(() => {
    if (skipFetch) {
      return;
    }

    const missingReferences = [];
    passages.forEach((p) => {
      const translationId = p.translation_id || p.translationId;
      const bookId = p.book_id || p.bookId;
      const chapterNum = p.chapter_number || p.chapterNumber;
      const startVerseNumber = p.start_verse_number || p.startVerseNumber;
      const endVerseNumber = p.end_verse_number || p.endVerseNumber;

      if (translationId && bookId && chapterNum && startVerseNumber && endVerseNumber) {
        // it shouldn't be often that any of these are null/undefined, but when they are, don't do a fetch
        const globalStoreVerses = verses?.[translationId]?.books?.[bookId]?.chapters?.[chapterNum]?.verses;
        const missingVerseNumbers = [];
        for (let verseNum = startVerseNumber; verseNum <= endVerseNumber; verseNum++) {
          let verse = globalStoreVerses?.[verseNum];
          if (verse === undefined || verse === null) { // @TODO: is this sufficient? (watch out for empty verse text)
            missingVerseNumbers.push(verseNum);
          }
          if (verse?.rangeStartVerseNumber && verse?.rangeEndVerseNumber) {
            for (let i = verse.rangeStartVerseNumber; i <= verse.rangeEndVerseNumber; i++) {
              // if verse in range, get those verses as well
              verse = globalStoreVerses?.[verseNum];
              if ((verse === undefined || verse === null) && !missingVerseNumbers.includes(i)) { // @TODO: is this sufficient? (watch out for empty verse text)
                missingVerseNumbers.push(verseNum);
              }
            }
          }
        }

        if (missingVerseNumbers.length) {
          missingReferences.push([translationId, bookId, chapterNum, missingVerseNumbers]);
        }
      }
    });

    if (missingReferences.length) {
      dispatch(fetchVersesBatched(missingReferences));
    }
  }, [passagesKey, skipFetch, dispatch, verses]); /* eslint-disable-line react-hooks/exhaustive-deps */ // @TODO: if `passages` is in the dep array, it's different on every re-render // @TODO: dep array should include translation.updated_at timestamp (do at the verse level?)

  return useMemo(() => {
    const versesByPassageId = {};
    return passages.reduce((acc, p) => {
      const translationId = p.translation_id || p.translationId; // @TODO: can't read `translation_id` of `undefined`
      const bookId = p.book_id || p.bookId;
      const book = books.find((b) => b.id === bookId);
      const chapterNum = p.chapter_number || p.chapterNumber;
      const startVerseNumber = p.start_verse_number || p.startVerseNumber;
      const endVerseNumber = p.end_verse_number || p.endVerseNumber;

      const retVerses = [];
      for (let verseNum = startVerseNumber; verseNum <= endVerseNumber; verseNum++) {
        const loadingVerse = {
          isLoading: true,
          verseNum, // @TODO: does this need to be here?
          verseApiId: `${book?.api_id}.${chapterNum}.${verseNum}`,
          translationId,
        }; // @TODO: maybe create a Verse class?
        const verseInRedux = verses?.[translationId]?.books?.[bookId]?.chapters?.[chapterNum]?.verses?.[verseNum];
        retVerses.push(!verseInRedux ? loadingVerse : {...verseInRedux, translationId});

        if (verseInRedux?.rangeStartVerseNumber && verseInRedux?.rangeEndVerseNumber) {
          for (let i = verseInRedux.rangeStartVerseNumber; i <= verseInRedux.rangeEndVerseNumber; i++) {
            // if verse in range, get those verses as well
            const loadingVerseRange = {
              isLoading: true,
              verseNum: i, // @TODO: does this need to be here?
              verseApiId: `${book?.api_id}.${chapterNum}.${i}`,
              translationId,
            }; // @TODO: maybe create a Verse class?
            const verseInReduxRange = verses?.[translationId]?.books?.[bookId]?.chapters?.[chapterNum]?.verses?.[i];
            if (!retVerses.find((v) => v.verseApiId === verseInReduxRange?.verseApiId)) {
              retVerses.push(!verseInReduxRange ? loadingVerseRange : {...verseInReduxRange, translationId});
            }
          }
        }
      }
      versesByPassageId[p.id] = retVerses;

      return {...acc, [p.id]: retVerses};
    }, {});
  }, [passagesKey, books, verses]); /* eslint-disable-line react-hooks/exhaustive-deps */ // if `passages` is in the dep array, it's different on every re-render
}
